import { Bone, Slot, Spine, TrackEntry } from '@esotericsoftware/spine-pixi'
import { playSpine, sleep, soundManager, spineManager } from '@slot-nx/core'
import { SpineGroupType } from '../game/enum/SpineGroupType'
import { Container, Sprite, Texture } from 'pixi.js'
import gsap from 'gsap'
import { useGameStore } from '../game/store/GameStore'
import { spinService } from '../game'
import { SymbolType } from '../game/enum/SymbolType'
import { createSymbolContainer, getMultiplierLv } from '../game/util/gameUtil'
import { gameConfig } from '../../config'
import { FreeGameType, GiftedSymbol, Multiplier, PotType } from '../game/model'
import { winningService } from '../winning/WinningService'
import { GameSoundType } from '../sound/GameSoundType'
import { getPotLevel, loadPotsFromStorage, POT_MAX_LEVEL, POT_MIN_LEVEL, potMap } from '../pot'

type PotColor = 'R' | 'G' | 'B'

// 為了清晰起見，定義一個設定檔的結構
interface PotConfig {
  type: PotType
  color: PotColor
  boneName: string
  skin: string
  spine?: Spine // 用來存放建立好的 spine
  boneIndex?: number // 為了效能，快取骨骼的 index
  tipSpine?: Spine
  /** Derived from the persisted {@link SlotPot} record — never assign directly. */
  readonly level: number
  hasLvDown: boolean
}

/**
 * `level` is a live getter over the persisted {@link SlotPot} record, so the
 * spine animations and sessionStorage can never drift apart.
 */
function createPotConfig(
  type: PotType,
  color: PotColor,
  boneName: string,
  skin: string,
): PotConfig {
  return {
    type,
    color,
    boneName,
    skin,
    hasLvDown: false,
    get level() {
      return getPotLevel(type)
    },
  }
}

/** Combo symbol type → the pot it feeds (66 = R, 88 = G, 77 = B). */
const SYMBOL_TYPE_TO_POT_TYPE: Record<number, PotType> = {
  [SymbolType.C1Expanding]: PotType.Expanding1, // 66 → R
  [SymbolType.C1Wild]: PotType.Wild2, // 88 → G
  [SymbolType.C1Multiplier]: PotType.Multiplier3, // 77 → B
}

const freeGameMap: Record<FreeGameType, number[]> = {
  [FreeGameType.None]: [],
  [FreeGameType.expend]: [0], // R
  [FreeGameType.wild]: [1], // G
  [FreeGameType.multiplier]: [2], // B
  [FreeGameType.wildExpend]: [0, 1], // R + G
  [FreeGameType.expendMultiplier]: [0, 2], // R + B
  [FreeGameType.multiplierWild]: [1, 2], // G + B
  [FreeGameType.wildExpendMultiplier]: [0, 1, 2], // R + G + B
}

class TableService {
  private _backgroundSpine: Spine
  private _zeusSpine: Spine
  private _tableSpine: Spine
  private _potContainer: Container
  private _slotContainer: Container
  private _upperContainer: Container
  public upperContainer2: Container
  public lowerId = 0

  public potList: PotConfig[] = [
    createPotConfig(PotType.Expanding1, 'R', 'bind_potTipR', 'red'),
    createPotConfig(PotType.Wild2, 'G', 'bind_potTipG', 'green'),
    createPotConfig(PotType.Multiplier3, 'B', 'bind_potTipB', 'blue'),
  ]

  // 依賴項的 Setters
  public set backgroundSpine(backgroundSpine: Spine) {
    this._backgroundSpine = backgroundSpine
  }

  public set setTableSpine(tableSpine: Spine) {
    this._tableSpine = tableSpine
    this.createPots().catch(err => console.error('[Pot] createPots failed', err))
    // setTimeout(() => {
    //   this.expandTable()
    // }, 1000)
    // setTimeout(() => {
    //   this._tableSpine.state.setAnimation(0, 'expand_out', false)
    // }, 2000)
  }

  public set zeusSpine(zeusSpine: Spine) {
    this._zeusSpine = zeusSpine
  }

  public setPotContainer(container: Container) {
    this._potContainer = container
  }

  public setSlotContainer(container: Container) {
    this._slotContainer = container
  }

  public setUpperContainer(container: Container) {
    this._upperContainer = container
  }

  public setUpperContainer2(container: Container) {
    this.upperContainer2 = container
  }

  public getSlotConatiner() {
    return this._slotContainer
  }

  public animateSlotHeight() {
    const slotConatinerMask = this._slotContainer.mask
    gsap.fromTo(
      slotConatinerMask,
      { height: gameConfig.expandReelHeight },
      {
        height: gameConfig.containerHegiht,
        duration: 0.8,
        ease: 'power1.inOut',
        onUpdate: () => {
          if (slotConatinerMask && 'scale' in slotConatinerMask) slotConatinerMask.scale.y = 1
        },
      },
    )
  }

  public async createPots(): Promise<void> {
    if (!this._tableSpine || !this._potContainer) return

    // Restore persisted pot progress before the spines pick their loop anim,
    // so a page refresh resumes at the level the player left off at.
    await loadPotsFromStorage()

    for (const potItem of this.potList) {
      const { color, boneName, skin, level } = potItem
      const bindPotSlotName = `bind_pot${color}`
      const bindPotTipSlotName = `bind_potTip${color}`

      const spineSlot = this._tableSpine.skeleton.findSlot(bindPotSlotName) as Slot
      const potTipSlot = this._tableSpine.skeleton.findSlot(bindPotTipSlotName) as Slot

      const potSpine = spineManager.create('pot.skel', 'pot.atlas', boneName, SpineGroupType.Pot, skin) as Spine
      potSpine.state.setAnimation(0, `lv${level}_loop`, true)

      const potTipSprite = new Sprite(Texture.from(`image_potTip${color}_close`))
      potTipSprite.anchor.set(0.5)
      const potTipSpine = spineManager.create('potTip.skel', 'potTip.atlas', boneName, SpineGroupType.Pot, skin) as Spine
      potTipSpine.state.setAnimation(0, 'close', true)

      this._tableSpine.addSlotObject(spineSlot, potSpine)
      this._tableSpine.addSlotObject(potTipSlot, potTipSpine)

      potItem.spine = potSpine
      potItem.tipSpine = potTipSpine
    }
  }

  public generateUpperReelsNode() {
    this._upperContainer.destroyChildren()
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 5; j++) {
        this._upperContainer.addChild(this.generateSingleUpperReelNode(i, j))
      }
    }
  }

  public generateExpendUpperReelsNode() {
    this._upperContainer.destroyChildren()
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 6; j++) {
        this._upperContainer.addChild(this.generateExpandSingleUpperReelNode(i, j))
      }
    }
  }

  public resetUpperReelsNode() {
    this._upperContainer.children.forEach((child) => {
      if (child instanceof Container) {
        while (child.children.length > 0) {
          child.destroyChildren()
        }
      }
    })
  }

  public generateUpperReelsNodeV2(name: string, x: number, y: number) {
    if (!this.upperContainer2) return
    const reelContainer = new Container()
    const defaultY = 57.5
    reelContainer.name = name
    reelContainer.x = x * gameConfig.nodeWidth
    reelContainer.y = defaultY + (y * gameConfig.nodeHeight)
    this.upperContainer2.addChild(reelContainer)
    return reelContainer
  }

  private generateExpandSingleUpperReelNode(i: number, j: number, name = 'UpperReelsNode') {
    const container = new Container()
    container.x = i * gameConfig.nodeWidth - 3
    container.y = (j - 1) * gameConfig.nodeHeight + 57.5
    container.name = `${name}_${i}_${j}`
    return container
  }

  private generateSingleUpperReelNode(i: number, j: number, name = 'UpperReelsNode') {
    const container = new Container()
    container.x = i * gameConfig.nodeWidth - 3
    container.y = j * gameConfig.nodeHeight + 57.5
    container.name = `${name}_${i}_${j}`
    return container
  }

  private update() {
    if (!this._tableSpine) return

    this.potList.forEach((pot) => {
      if (pot.spine && pot.boneIndex !== undefined) {
        const bone = this._tableSpine.skeleton.bones[pot.boneIndex]
        pot.spine.position.set(bone.worldX, bone.worldY)
      }
    })
  }

  public startFreeGame() {
    this._backgroundSpine.state.setAnimation(0, 'bonus', true)
    useGameStore.setState({ isFreeGame: true })
  }

  public endFreeGame() {
    this._backgroundSpine.state.setAnimation(0, 'main', true)
    useGameStore.setState({ isFreeGame: false })
  }

  public potAnimation(index: number, level: number, action: string): Promise<void> {
    return new Promise((resolve) => {
      const potData = this.potList[index]
      const potItem = this.potList.find(pot => pot.boneName === potData.boneName) as PotConfig
      const spine = potItem.spine as Spine
      const upAnim = `lv${level}_${action}`
      const loopAnim = `lv${level}_loop`

      // 播 Animation
      spine.state.setAnimation(0, upAnim, false)

      // 監聽動畫完成
      const listener = {
        complete: () => {
          spine.state.removeListener(listener)
          spine.state.addAnimation(0, loopAnim, true, 0)
          resolve()
        },
      }

      spine.state.addListener(listener)
    })
  }

  public zeusWin() {
    this._zeusSpine.state.setAnimation(0, 'potWin', false)
    this._zeusSpine.state.addAnimation(0, 'loop', true, 0)
  }

  public zeusExpand() {
    this._zeusSpine.state.setAnimation(0, 'expend_in', false)
    this._zeusSpine.state.addAnimation(0, 'expend_loop', true, 0)
  }

  public async tableRemove(removeList: number[][]) {
    const gameStore = useGameStore.getState()
    const dropTable = gameStore.getDropItem().finalTable
    const reelContainers = spinService.reelNodes
    const taskArr: Promise<void>[] = []

    for (let col = 0; col < removeList.length; col++) {
      const rows = removeList[col].sort((a, b) => b - a)
      for (const row of rows) {
        const symbolData = dropTable[col]?.[row]
        const container = reelContainers[col].children[row] as Container
        const nodeName = container.name
        const upperNode = this._upperContainer.getChildByName(`UpperReelsNode_${col}_${row}`) as Container
        const upperChild = this.upperContainer2.getChildByName(`${nodeName}`)
        const skinName = SymbolType[symbolData.type]
        const task = this._playSpineAt(upperNode, 'symbolSpine', 'clear', skinName, false)
        upperChild?.destroy()
        container.destroy()
        taskArr.push(task)
      }
    }
    soundManager.play(GameSoundType.ThreeComboOlympus_SymbolClear)
    await Promise.all(taskArr)
    useGameStore.setState({ hasRefillTable: true })
    const dropIndex = gameStore.dropIndex
    const payAmount = gameStore.getGameResult().dropList[dropIndex].payout
    winningService.updateTumbleWin(payAmount)
  }

  public async playDropAnimation(removeList: number[][], giftList: GiftedSymbol[]) {
    const gameStore = useGameStore.getState()
    const { getDropItem, isFreeGame } = gameStore

    const dropItem = getDropItem()
    const dropTable = dropItem.dropTable
    const reelContainers = spinService.reelNodes
    const delayPerStep = 12.5
    const delayPerSymbol = 0.075
    const nodeHeight = 116
    const nodeCount = gameStore.isExpand ? 5 : 4

    const maxBottom = Math.max(...removeList.map(arr => arr.length ? arr[arr.length - 1] : -1))
    const dropPromises = dropTable.map((dropItem, col) => {
      const removeArr = removeList[col] || []
      if (!dropItem.length) return Promise.resolve()
      const container = reelContainers[col]
      const columnBottom = removeArr.length ? removeArr[removeArr.length - 1] : 0
      const columnDelay = (maxBottom - columnBottom) * delayPerStep
      if (!isFreeGame) {
        const GiftedSymbol = giftList.filter(g => g.position.x === col)
        if (GiftedSymbol) spinService._playGiftSymbol(GiftedSymbol)
      }
      return new Promise<void>((resolve) => {
        const removedCount = dropItem.length
        dropItem.forEach((symbolData, index) => {
          const newSymbol = createSymbolContainer(symbolData)
          newSymbol.y = -(removedCount - index) * nodeHeight
          newSymbol.name = `NodeId_${this.lowerId++}`
          container.addChild(newSymbol)
        })
        const allChildren = container.children.sort((a, b) => b.y - a.y)
        let completed = 0
        allChildren.forEach((child, index) => {
          const nodeName = child.name
          const upperChild = this.upperContainer2.getChildByName(`${nodeName}`)
          const baseY = (nodeCount - index) * nodeHeight
          const upperY = (nodeCount - index) * nodeHeight + 57.5

          if (child.y === baseY) {
            completed++
            if (completed === allChildren.length) {
              resolve()
            }
            return
          }
          const overshootY = baseY + 10
          const settleY = baseY - 10

          const tl = gsap.timeline({
            delay: index * delayPerSymbol + columnDelay / 1000,
            onComplete: () => {
              (async () => {
                completed++
                if (completed === allChildren.length) {
                  // ⭐ 顯示 gift 暫時隱藏 symbols
                  resolve()
                }
              })()
            },
          })

          // 底部 symbol

          tl.to(child, { y: overshootY, duration: 0.15, ease: 'power2.out' })
          this.moveIfExist(tl, upperChild, { y: upperY + 10, duration: 0.15, ease: 'power2.out' }, '<')
          tl.to(child, { y: settleY, duration: 0.12, ease: 'power1.in' })
          this.moveIfExist(tl, upperChild, { y: upperY - 10, duration: 0.12, ease: 'power1.in' }, '<')
          tl.to(child, { y: baseY, duration: 0.12, ease: 'power1.out' })
          this.moveIfExist(tl, upperChild, { y: upperY, duration: 0.12, ease: 'power1.out' }, '<')
          // tl.to(child, { y: overshootY, duration: 0.15, ease: 'power2.out' })
          //   .to(upperChild, { y: upperY + 10, duration: 0.15, ease: 'power2.out' }, '<')
          //   .to(child, { y: settleY, duration: 0.12, ease: 'power1.in' })
          //   .to(upperChild, { y: upperY - 10, duration: 0.12, ease: 'power1.in' }, '<')
          //   .to(child, { y: baseY, duration: 0.12, ease: 'power1.out' })
          //   .to(upperChild, { y: upperY, duration: 0.12, ease: 'power1.out' }, '<')
        })
      })
    })
    soundManager.play(GameSoundType.ThreeComboOlympus_SymbolReplenish)
    await Promise.all(dropPromises)
    if (giftList.length) {
      await spinService._playGiftSymbol(giftList)
      spinService.applyGiftSymbolsToReels(giftList)
    }
    this.resetUpperReelsNode()
    reelContainers.forEach(container => this._reorderChildren(container))
    await sleep(100) // 等待微調完成 _reorderChildren可能還沒好，就playPotSymbolTips 可能會加錯container
    // ⭐ 落下動畫完成後，收集 pot symbols 資訊
    const potTasks: Promise<void>[] = []
    dropTable.forEach((colData, col) => {
      colData.forEach((symbol, row) => {
        if ([66, 77, 88].includes(symbol.type)) {
          const nodeName = spinService.reelNodes[col].children[row].name || ''
          const upperNode2 = this.generateUpperReelsNodeV2(nodeName, col, row) as Container
          potTasks.push(spinService.playPotSymbolTips(symbol.type, upperNode2))
        }
      })
    })

    if (potTasks.length) {
      soundManager.play(GameSoundType.ThreeComboOlympus_SymbolTip)
      await Promise.all(potTasks)
    }
  }

  private moveIfExist = (tl: gsap.core.Timeline, target: any | undefined, vars: gsap.TweenVars, position?: gsap.Position) => {
    if (!target) return
    tl.to(target, vars, position)
  }

  private _reorderChildren(container: Container) {
    container.children.sort((a, b) => a.y - b.y)
  }

  private _playSpineAt(container: Container, assetName: string, animationName: string, skin: string, loop = false, option?: (spine: Spine) => void): Promise<void> {
    return new Promise<void>((resolve) => {
      const spine = spineManager.create('symbol.skel', 'symbol.atlas', assetName, SpineGroupType.Symbol)
      spine.skeleton.setSkinByName(skin)
      container.destroyChildren()
      container.addChild(spine)
      if (option) option(spine)
      spine.state.setAnimation(0, animationName, loop)
      if (!loop) {
        spine.state.addListener({
          complete: () => {
            spine.destroy()
            container.destroyChildren()
            resolve()
          },
        })
      }
      else {
        resolve()
      }
    })
  }

  public async playSymbolSpine(x: number, y: number, spineName: string, assetName: string, multiplierData: Multiplier) {
    const reelContainers = spinService.reelNodes
    const container = reelContainers[x].children[y] as Container
    const upperNode = this._upperContainer.getChildByName(`UpperReelsNode_${x}_${y}`) as Container
    const skin = getMultiplierLv(multiplierData.multiplier)
    container.visible = false
    await this._playSpineAt(upperNode, assetName, spineName, skin, false)
    container.visible = true
  }

  public checkCollectCombo() {
    const comboList: { x: number, y: number, type: number, name: string }[] = []
    const finalTable = useGameStore.getState().getDropItem().finalTable
    const typeNameMap: Record<number, string> = {
      66: 'CollectionR',
      77: 'CollectionB',
      88: 'CollectionG',
    }

    finalTable.forEach((reel, x) => {
      reel.forEach((symbol, y) => {
        if (symbol?.type === 66 || symbol?.type === 77 || symbol?.type === 88) {
          comboList.push({ x, y, type: symbol.type, name: typeNameMap[symbol.type] ?? '' })
        }
      })
    })
    return comboList
  }

  /**
   * Collect the combo symbols into their pots. Level-ups are computed on the
   * frontend from the persisted pot state (the backend `potActionList` is no
   * longer consulted).
   */
  public async collectCombo(comboList: { x: number, y: number, type: number, name: string }[]) {
    // Snapshot levels BEFORE resolving, so the `tips` animation still shows the
    // pre-level-up pot; `potLvUp` below plays the transition afterwards.
    const levelBeforeCollect = new Map<PotType, number>(
      this.potList.map(pot => [pot.type, pot.level]),
    )
    const potActionList = this.resolvePotActions(comboList)
    const tasks = comboList.map(async (item) => {
      const { x, y, name } = item
      const node = spinService.reelNodes[x].children[y] as Container
      const upperNode = this._upperContainer.getChildByName(`UpperReelsNode_${x}_${y}`) as Container
      const potColor = name.charAt(name.length - 1) as 'R' | 'G' | 'B'
      const potItem = this.potList.find(p => p.color === potColor) as PotConfig
      node.visible = true
      this._playSpineAt(upperNode, 'symbolSpine', 'tips', name, false, (spine) => { // symbol tips
        const bone = spine.skeleton.findBone('bind_target') as Bone
        if (potItem?.spine) {
          const potPosition = potItem.spine.getGlobalPosition()
          const localPotPosition = spine.toLocal(potPosition)
          bone.x = localPotPosition.x
          bone.y = -(localPotPosition.y - 100)
        }
      })
      await sleep(1000)
      const tipsLevel = levelBeforeCollect.get(potItem.type) ?? potItem.level
      this.playPotAnim(potItem, tipsLevel, 'tips', true, false, tipsLevel) // pot tips
    })
    soundManager.play(GameSoundType.ThreeComboOlympus_PotCollect)
    this.upperContainer2.destroyChildren()
    await Promise.all(tasks)
    if (potActionList.length) await this.potLvUp(potActionList)
  }

  /**
   * @param loopLevel - Level for the follow-up loop animation. Defaults to the
   *   pot's current (post-collect) level; pass explicitly when the loop must
   *   stay on the pre-level-up level, e.g. the `tips` animation.
   */
  private playPotAnim(potItem: PotConfig, level: number, action: string, loop = true, showBgPot = false, loopLevel?: number): Promise<void> {
    return new Promise((resolve) => {
      const spine = potItem.spine as Spine
      if (!spine) return resolve()
      const upAnim = `lv${level}_${action}`
      const loopAnim = showBgPot ? 'bgPot' : `lv${loopLevel ?? potItem.level}_loop`
      spine.state.setAnimation(0, upAnim, false)
      const listener = {
        complete: (entry: TrackEntry) => {
          if (entry?.animation?.name !== upAnim) return
          spine.state.removeListener(listener)
          if (loop) spine.state.setAnimation(0, loopAnim, true)
          resolve()
        },
      }
      spine.state.addListener(listener)
    })
  }

  /**
   * Feed the collected symbols into their pots and return the resulting
   * level-up actions. This is the frontend replacement for the backend's
   * `potActionList` — collecting mutates (and persists) the pot records.
   */
  private resolvePotActions(comboList: { type: number, name: string }[]): { type: number, from: number, to: number }[] {
    const countByPot = new Map<PotType, number>()
    for (const item of comboList) {
      const potType = SYMBOL_TYPE_TO_POT_TYPE[item.type]
      if (potType === undefined) continue
      countByPot.set(potType, (countByPot.get(potType) ?? 0) + 1)
    }

    const actions: { type: number, from: number, to: number }[] = []
    for (const [potType, count] of countByPot) {
      const [isLevelUp, action] = potMap[potType].collect(count)
      if (isLevelUp && action) {
        actions.push({ type: potType, from: action.from, to: action.to })
      }
    }
    return actions
  }

  public async potLvUp(potActionList: { type: number, from: number, to: number }[]) {
    const tasks: Promise<void>[] = []
    for (const action of potActionList) {
      const potItem = this.potList.find(p => p.type === action.type)
      if (!potItem) continue
      const actionName = action.to - action.from === 1 ? 'upLv' : 'upLvMax'
      const task = this.playPotAnim(potItem, action.from, actionName, true, false, action.to)
      tasks.push(task)
    }
    await Promise.all([...tasks, soundManager.play(GameSoundType.ThreeComboOlympus_PotLvUp)])
  }

  public async potLvDown() {
    for (const potItem of this.potList) {
      if (!potItem.hasLvDown) continue
      const spine = potItem.spine as Spine
      potMap[potItem.type].clear()
      spine.state.setAnimation(0, `lv${POT_MIN_LEVEL}_loop`, true)
      potItem.hasLvDown = false
    }
  }

  public async potInFg(freeGameType: FreeGameType, isRetrigger = false) {
    const potIndexes = freeGameMap[freeGameType]
    if (!potIndexes || potIndexes.length === 0) return

    const tasks = potIndexes.map(async (i) => {
      const potItem = this.potList[i]
      potMap[potItem.type].forceLevelUp(POT_MAX_LEVEL)
      potItem.hasLvDown = true
      potItem.tipSpine?.state.setAnimation(0, 'open', true)
      soundManager.play(GameSoundType.ThreeComboOlympus_PotTrigger)
      await this.playPotAnim(potItem, POT_MAX_LEVEL, 'win', true, true)
    })
    if (!isRetrigger) soundManager.play(GameSoundType.ThreeComboOlympus_Ring)
    await Promise.all(tasks)
  }

  public getUpperNode(x: number, y: number) {
    const upperNode = this._upperContainer.getChildByName(`UpperReelsNode_${x}_${y}`) as Container
    return upperNode
  }

  public getUpperNode2(col: number, row: number) {
    const reel = this.upperContainer2?.children[col] as Container
    return reel?.children[row] as Container
  }

  public async expandTable() {
    tableService.zeusExpand()
    await sleep(500)
    soundManager.play(GameSoundType.ThreeComboOlympus_FrameExpand)
    await playSpine(this._tableSpine, 'expand_in', false)
    await spinService.tableExpand()
    playSpine(this._tableSpine, 'expand_loop', true)
    this.generateExpendUpperReelsNode()
    useGameStore.setState({ isExpand: true })
    await sleep(500)
  }

  public async removeExpandTable() {
    this.upperContainer2.y += gameConfig.nodeHeight
    this._zeusSpine.state.setAnimation(0, 'expend_out', false)
    playSpine(this._tableSpine, 'expand_out', false)
    this._zeusSpine.state.addAnimation(0, 'loop', true, 0)
    await sleep(500)
    await spinService.removeTableExpand()
    this.generateUpperReelsNode()
    useGameStore.setState({ isExpand: false })
    await sleep(200)
  }

  public async expandNormalTable() {
    this._tableSpine.state.setAnimation(0, 'expand_loopM', true)
  }
}

export const tableService = new TableService()
